import Image from 'next/image'
import { useRouter } from 'next/router'
import { ChevronLeft, Mail } from 'lucide-react'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import PageLayout from '@Components/layout/pageLayout'
import { ButtonWithIcon } from '@Components/button'
import Typography from '@Components/typo'
import { getNavbar } from '../../../apis/navbar'
import { getProductBySlug } from '../../../apis/product/productBySlug'
import { CarouselContainer } from '@Components/carousel'
import { CarouselItem } from '@Components/ui/carousel'
import Divider from '@Components/divider'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import { getFooter } from '../../../apis/footer'
import LineIcon from '../../../../public/images/icon-line-button.svg'
import IGIcon from '../../../../public/images/icon-ig-button.svg'
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import { Seo } from '@Components/seo'

const Page = ({
  navbar,
  product,
  footer,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const router = useRouter()
  const { t } = useTranslation('common')

  const handleBack = () => {
    router.back()
  }

  const pageCount = product.Image?.length
  return (
    <>
      <Seo
        title={product.Seo?.MetaTitle}
        description={product.Seo?.MetaDescription}
        keywords={product.Seo?.Keywords}
        canonicalURL={product.Seo?.CanonicalURL || ''}
      />
      <PageLayout
        navbar={navbar}
        footer={footer}
        className='bg-content-on-color-background'
      >
        <div className='pb-16'>
          <ButtonWithIcon
            variant='tertiary'
            leftIcon={<ChevronLeft />}
            className='w-fit'
            onClick={handleBack}
          >
            {t('product.back')}
          </ButtonWithIcon>
          <div className='flex flex-col gap-8 pt-8'>
            <Typography size='h4' weight='bold'>
              {product.Title}
            </Typography>
            <div className='flex flex-col justify-center gap-8 lg:flex-row'>
              <div className='w-full lg:w-1/2'>
                <CarouselContainer pageCount={pageCount} dataLength={pageCount}>
                  {Array.from({ length: pageCount }).map((_, index) => (
                    <CarouselItem
                      key={index}
                      className='mb-8 flex justify-center p-8'
                    >
                      <Image
                        src={product.Image[index].url}
                        alt=''
                        width={100}
                        height={100}
                        className='h-[540px] w-[540px]'
                      />
                    </CarouselItem>
                  ))}
                </CarouselContainer>
              </div>
              <div className='flex w-full flex-col gap-8 lg:w-1/2'>
                {product?.Description && (
                  <div className='flex flex-col gap-4'>
                    <Typography weight='bold'>
                      {t('product.description')}
                    </Typography>
                    <Typography>{product.Description}</Typography>
                  </div>
                )}
                {product.InTheBox?.length > 0 && (
                  <div className='flex flex-col gap-4'>
                    <Typography weight='bold'>
                      {t('product.inTheBox')}
                    </Typography>
                    <div>
                      {product.InTheBox[0].children.map((item, index) => (
                        <li key={index}>{item.children[0].text}</li>
                      ))}
                    </div>
                  </div>
                )}
                {product.AddOns?.map((item, index) => (
                  <div
                    key={index}
                    className='rounded-2xl border-[1px] border-solid border-divider p-3'
                  >
                    <div className='flex flex-row gap-3'>
                      <Image
                        src={item.Image?.url}
                        alt=''
                        width={100}
                        height={100}
                        className='h-[74px] w-[74px] p-[13px]'
                      />
                      <div className='flex flex-col gap-2'>
                        <Typography weight='bold' size='base'>
                          {item.Title}
                        </Typography>
                        <Typography size='small'>{item.Detail}</Typography>
                      </div>
                    </div>
                  </div>
                ))}
                <div className='flex flex-row items-center gap-8 rounded-2xl bg-neutral-level-2-default p-8'>
                  {product.LineLink && (
                    <Link href={product?.LineLink}>
                      <ButtonWithIcon
                        variant='line'
                        leftIcon={
                          <Image
                            src={LineIcon}
                            alt=''
                            className='h-icon-lg w-icon-lg'
                          />
                        }
                      >
                        {product.LineTitleButton}
                      </ButtonWithIcon>
                    </Link>
                  )}
                  {product.IGLink && (
                    <Link href={product?.IGLink}>
                      <ButtonWithIcon
                        variant='ig'
                        leftIcon={
                          <Image
                            src={IGIcon}
                            alt=''
                            className='h-icon-lg w-icon-lg'
                          />
                        }
                      >
                        {product.IGTitleButton}
                      </ButtonWithIcon>
                    </Link>
                  )}
                </div>
                <Divider />
                <div className='flex flex-col gap-4'>
                  <Typography weight='bold'>
                    {t('product.specification')}
                  </Typography>

                  {product?.InputVoltage && (
                    <div className='flex flex-col gap-1 md:flex-row'>
                      <Typography
                        size='small'
                        weight='bold'
                        className='min-w-full max-w-[110px] md:min-w-[200px] md:max-w-[200px]'
                      >
                        {t('product.inputVoltage')}
                      </Typography>
                      <Typography size='small'>
                        {product.InputVoltage}
                      </Typography>
                    </div>
                  )}

                  {product?.OutputVoltage && (
                    <div className='flex flex-col gap-1 md:flex-row'>
                      <Typography
                        size='small'
                        weight='bold'
                        className='min-w-full max-w-[110px] md:min-w-[200px] md:max-w-[200px]'
                      >
                        {t('product.outputVoltage')}
                      </Typography>
                      <Typography size='small'>
                        {product.OutputVoltage}
                      </Typography>
                    </div>
                  )}

                  {product?.Connector && (
                    <div className='flex flex-col gap-1 md:flex-row'>
                      <Typography
                        size='small'
                        weight='bold'
                        className='min-w-full max-w-[110px] md:min-w-[200px] md:max-w-[200px]'
                      >
                        {t('product.connector')}
                      </Typography>
                      <Typography size='small'>{product.Connector}</Typography>
                    </div>
                  )}

                  {product?.ChargingCableLength && (
                    <div className='flex flex-col gap-1 md:flex-row'>
                      <Typography
                        size='small'
                        weight='bold'
                        className='min-w-full max-w-[110px] md:min-w-[200px] md:max-w-[200px]'
                      >
                        {t('product.chargingCableLength')}
                      </Typography>
                      <Typography size='small'>
                        {product.ChargingCableLength}
                      </Typography>
                    </div>
                  )}

                  {product?.Standard && (
                    <div className='flex flex-col gap-1 md:flex-row'>
                      <Typography
                        size='small'
                        weight='bold'
                        className='min-w-full max-w-[110px] md:min-w-[200px] md:max-w-[200px]'
                      >
                        {t('product.standard')}
                      </Typography>
                      <Typography size='small'>{product.Standard}</Typography>
                    </div>
                  )}

                  {product?.DustAndWaterResistant && (
                    <div className='flex flex-col gap-1 md:flex-row'>
                      <Typography
                        size='small'
                        weight='bold'
                        className='min-w-full max-w-[110px] md:min-w-[200px] md:max-w-[200px]'
                      >
                        {t('product.dustAndWaterResistant')}
                      </Typography>
                      <Typography size='small'>
                        {product.DustAndWaterResistant}
                      </Typography>
                    </div>
                  )}

                  {product?.Dimension && (
                    <div className='flex flex-col gap-1 md:flex-row'>
                      <Typography
                        size='small'
                        weight='bold'
                        className='min-w-full max-w-[110px] md:min-w-[200px] md:max-w-[200px]'
                      >
                        {t('product.dimension')}
                      </Typography>
                      <Typography size='small'>{product.Dimension}</Typography>
                    </div>
                  )}

                  {product?.Weight && (
                    <div className='flex flex-col gap-1 md:flex-row'>
                      <Typography
                        size='small'
                        weight='bold'
                        className='min-w-full max-w-[110px] md:min-w-[200px] md:max-w-[200px]'
                      >
                        {t('product.weight')}
                      </Typography>
                      <Typography size='small'>{product.Weight}</Typography>
                    </div>
                  )}
                </div>
                <Divider />
                {product?.Warranty && (
                  <div className='flex flex-col gap-4'>
                    <Typography weight='bold'>
                      {t('product.warranty')}
                    </Typography>
                    <Typography size='small'>{product.Warranty}</Typography>
                  </div>
                )}
                <Divider />
                {product?.SupportTitle && (
                  <div className='flex flex-col gap-4'>
                    <Typography weight='bold'>
                      {t('product.support')}
                    </Typography>
                    <Typography
                      size='small'
                      element='a'
                      color='text-link-default'
                      href={product.SupportLink}
                    >
                      {product.SupportTitle}
                    </Typography>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </PageLayout>
    </>
  )
}

export default Page

export const getServerSideProps = async ({
  locale = 'en',
  params,
}: GetServerSidePropsContext) => {
  const { slug } = params as { slug: string }

  const [navbar, product, footer] = await Promise.all([
    getNavbar({ locale }),
    getProductBySlug({ locale, slug }),
    getFooter({ locale }),
  ])

  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      navbar: navbar[0],
      footer: footer[0],
      product: product[0],
    },
  }
}
